import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
`;
export const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;
export const Card = styled.section`
  padding: 20px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  min-width: 0;
`;
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;
export const Muted = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.6;
  margin: 6px 0;
  overflow-wrap: anywhere;
`;
export const Notice = styled.div<{ $error?: boolean }>`
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme, $error }) => ($error ? theme.colors.status.danger : theme.colors.text.primary)};
  border-left: 3px solid
    ${({ theme, $error }) => ($error ? theme.colors.status.danger : theme.colors.status.warning)};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  min-width: 180px;
  flex: 1;
`;
export const Input = styled.input`
  font: inherit;
  color: inherit;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  padding: 10px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`;
export const Select = styled.select`
  font: inherit;
  color: inherit;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  padding: 10px;
  max-width: 100%;
`;
export const Textarea = styled.textarea`
  font: inherit;
  color: inherit;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  padding: 10px;
  min-height: 90px;
  width: 100%;
  box-sizing: border-box;
`;
export const TableBox = styled.div`
  overflow: auto;
  max-height: 480px;
`;
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    overflow-wrap: anywhere;
    max-width: 320px;
  }
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    z-index: 1;
  }
  button {
    white-space: nowrap;
  }
`;
export const Nav = styled.nav`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
export const NavItem = styled(Link)<{ $active?: boolean }>`
  display: block;
  padding: 9px 12px;
  border-radius: 8px;
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.primary)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.surface)};
  text-decoration: none;
  font-size: 13px;
`;
export const Small = styled.small`
  display: block;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

/* ── Шапка рабочего места: название, метки, разделы ── */

export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  h1 {
    margin: 0;
    font-size: 26px;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }
`;
export const Spacer = styled.div`
  flex: 1;
`;
export const BackLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'accent';
const toneColor = (tone: Tone, theme: import('styled-components').DefaultTheme) =>
  ({
    neutral: theme.colors.text.secondary,
    ok: theme.colors.status.success,
    warn: theme.colors.status.warning,
    bad: theme.colors.status.danger,
    accent: theme.colors.primary,
  })[tone];

/** Небольшая метка статуса: цвет всегда с текстом, не вместо него. */
export const Chip = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
  color: ${({ theme, $tone = 'neutral' }) => toneColor($tone, theme)};
  border: 1px solid
    ${({ theme, $tone = 'neutral' }) =>
      $tone === 'neutral' ? theme.colors.borderStrong : toneColor($tone, theme)};
  background: ${({ theme, $tone = 'neutral' }) =>
    $tone === 'neutral' ? 'transparent' : theme.colors.surface};
`;

export const SectionNav = styled.nav`
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
`;
export const SectionLink = styled(Link)<{ $active?: boolean; $aside?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  font-size: 14px;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  margin-bottom: -1px;
  ${({ $aside }) =>
    $aside &&
    css`
      margin-left: auto;
    `}
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;
export const Count = styled.span<{ $tone?: Tone }>`
  min-width: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  color: ${({ theme, $tone = 'neutral' }) =>
    $tone === 'neutral' ? theme.colors.text.secondary : theme.colors.backgroundElevated};
  background: ${({ theme, $tone = 'neutral' }) =>
    $tone === 'neutral' ? theme.colors.surfaceHover : toneColor($tone, theme)};
`;
export const SubNav = styled.nav`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
export const SubLink = styled(Link)<{ $active?: boolean }>`
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  text-decoration: none;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/* ── Плитки сводки ── */

export const Tiles = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr 1fr 1fr;
  gap: 12px;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;
export const Tile = styled.div<{ $tone?: Tone }>`
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid
    ${({ theme, $tone }) => ($tone && $tone !== 'neutral' ? toneColor($tone, theme) : theme.colors.border)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  min-width: 0;
`;
export const TileLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;
export const TileValue = styled.div<{ $tone?: Tone }>`
  margin-top: 2px;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: ${({ theme, $tone }) => ($tone && $tone !== 'neutral' ? toneColor($tone, theme) : theme.colors.text.primary)};
`;
export const TileSub = styled.div`
  margin-top: 4px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;
export const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  align-items: start;
  gap: 12px;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;
